import { defa }            from 'defa-object';
import CustomerContainer   from 'scripts/defa/customers/view-customer-container';
import LegacyLoginForm     from 'scripts/defa/legacy/view-legacy-login-form';
import LegacyUnitContainer from 'scripts/defa/legacy/view-legacy-unit-container';
import UserNewAccountView  from 'scripts/defa/users/view-user-new-account-view';
import Flow                from 'scripts/poppet/flow';
import template            from 'templates/legacyFlow.jst.html';

/**
 * defa.views.LegacyFlow
 * The flow for legacy migration in Simplify.
 * @version 1.2.2
 */

const channelLayout = Bb.Radio.channel( 'layout' ),
	channelUser   = Bb.Radio.channel( 'user' ),
	channel       = Bb.Radio.channel( 'objects' );

const selectors = {
	regionContainer: '[data-region=legacy-container]',
};

const LegacyFlow = Flow.extend( {
	
	// ------------------------------------------------
	// Setup
	// ------------------------------------------------
	
	template: template,
	
	regions: {
		container: selectors.regionContainer,
	},
	
	
	// ------------------------------------------------
	// Event Handlers
	// ------------------------------------------------
	
	/**
	 * Triggers when a childView requests to open a legacyUnit.
	 * @param {int} id  The id of the legacyUnit.
	 * @void
	 */
	onChildviewLegacyUnitOpen: function ( id ) {
		
		this.legacyUnit = this.legacyUser.get( 'units' ).find( function ( model ) {
			return model.get( 'unitId' ) === id;
		} );
		
		this.dispatch();
	},
	
	/**
	 * Triggers when a childView has selected a customer.
	 * @void
	 */
	onChildviewCustomerOpen: function ( id ) {
		this.owner = this.customers.get( id );
		this.dispatch();
	},
	
	/**
	 * Triggers when the end user attempts to login as a legacy user.
	 * @void
	 */
	onChildviewLegacyLogin: function ( options ) {
		
		// is logged in:
		
		if ( channelUser.request( 'is:logged:in' ) ) {
			
			this.listenToOnce( this.legacyUser, 'sync', function () {
				this.dispatch();
			} );
			this.legacyUser.authenticate( options );
			
		} else {
			
			// is logged out:
			
			this.listenToOnce( this.legacyUser, 'user:created', function () {
				let userNewAccountView = new UserNewAccountView( {
					model: channelUser.request( 'model' ),
				} );
				
				this.showChildView( 'container', userNewAccountView );
				
			} );
			
			this.listenToOnce( this.legacyUser, 'user:found', function () {
				
				this.listenToOnce( this.legacyUser, 'sync', function () {
					this.dispatch();
				} );
				// should we just add options here, and not put user/password in the model earlier?
				
				this.legacyUser.authenticate();
			} );
			
			this.legacyUser.generateUser( options );
			
		}
		
		// either legacyUser authenticate,
		
		// or legacyUser fetchUser
	},
	
	/**
	 * Triggers when the end user accepts his new account.
	 * @void
	 */
	onChildviewAccountAccept: function ( childView ) {
		childView.busy();
		
		this.listenToOnce( this.legacyUser, 'sync', function () {
			this.stopListening( this.legacyUser, 'error' );
			this.dispatch();
		} );
		
		this.listenToOnce( this.legacyUser, 'error', function () {
			this.stopListening( this.legacyUser, 'sync' );
			channelLayout.trigger( 'error', { model: this.legacyUser } );
		} );
		
		this.legacyUser.authenticate();
	},
	
	// ------------------------------------------------
	// Other functions
	// ------------------------------------------------
	
	// - dispatches and flows
	
	/**
	 * Decides which view to show based on information available to the layout.
	 * @void
	 */
	dispatch: function () {
		
		this.selectUnit = this.selectUnit || this.options.selectUnit || false;
		this.first = this.first || this.options.first || false;
			
		this.legacyUser = this.legacyUser || this.options.legacyUser || channel.request( 'legacyUser' );
		this.legacyUnit = this.legacyUnit || false;
		
		// this user can be the technical support user
		this.user     = channelUser.request( 'model' );
		this.loggedIn = channelUser.request( 'is:logged:in' );
		this.support  = this.user.hasRoles( [ 'TECHNICAL_SUPPORT', ] );
		
		// if this is support, selectedUser is a User with the correct ID set
		this.selectedUser = this.selectedUser || this.options.selectedUser || this.user || false;
		
		this.owner  = this.owner || this.options.owner || false;
		this.email  = this.email || this.options.email || '';
		this.userId = this.userId || this.options.userId || false;
		
		// always try to get the customers anew if the selectedUser is set
		this.customers = ( _.isObject( this.selectedUser ) )
						 ? this.selectedUser.getCustomers()
						 : [];
		
		// make room for more legacy functionality
		switch ( this.options.context ) {
			
			case 'migrate' :
			default        :
				this._migrateDispatch();
				break;
		}
	},
	
	/**
	 * Dispatch for migrating a unit from legacy systems to the new systems.
	 * @void
	 */
	_migrateDispatch: function () {
		
		let fn = false;
		
		// legacy login
		if ( !this.legacyUser.hasUnits() ) {
			fn = this._showLegacyLogin;
			
			// this may trigger two requests :/
		}
		
		// select unit
		if ( this.loggedIn && this.legacyUser.hasUnits() && !this.legacyUnit ) {
			if ( 1 === this.legacyUser.get( 'units' ).length && !this.selectUnit ) {
				this.legacyUnit = this.legacyUser.get( 'units' ).models[ 0 ];
			} else {
				fn = this._showLegacyUnits;
			}
		}
		
		// select owner
		if ( this.loggedIn && this.legacyUser.hasUnits() && this.legacyUnit && !this.owner ) {
			if ( 1 === this.customers.length ) {
				this.owner = this.customers.models[ 0 ];
			} else {
				fn = this._showSelectOwner;
			}
		}
		
		// migrate unit
		if ( this.loggedIn && this.legacyUser.hasUnits() && this.legacyUnit && this.owner ) {
			fn = this._migrateUnit;
		}
		
		// run
		if ( _.isFunction( fn ) ) {
			fn.call( this );
		}
	},
	
	// legacy handling
	
	/**
	 * Displays a login form for legacy users.
	 * @void
	 */
	_showLegacyLogin: function () {
		
		let legacyLoginForm = new LegacyLoginForm( {
			model: this.legacyUser,
		} );
		
		this.showChildView( 'container', legacyLoginForm );
	},
	
	/**
	 * Shows a list of units for legacy users.
	 * @void
	 */
	_showLegacyUnits: function () {
		
		let legacyUnitContainer = new LegacyUnitContainer( {
			model: this.legacyUser,
			first: this.first,
		} );
		
		this.showChildView( 'container', legacyUnitContainer );
	},
	
	// customer handling
	
	/**
	 * Shows a customer verification screen.
	 * @void
	 */
	_showSelectOwner: function () {
		
		let self = this;
		
		let view = ( 0 === self.customers.length )
				   ? 'customer:create'
				   : 'customer:list';
		
		// allow select owner to trigger on a successful create
		let customer = self.legacyUser.get( 'customer' );
		
		self.listenTo( customer, 'sync', function () {
			self.owner = channel.request( 'customers' ).add( customer.attributes, { merge: true } );
			
			// if the mapping doesn't exist, add it
			if ( _.where( defa.vars.mapUsersCustomers, { user: self.selectedUser.get( 'id' ), customer: self.owner.get( 'id' ), } ).length !== 1 ) {
				defa.vars.mapUsersCustomers.push( {
					user    : self.selectedUser.get( 'id' ),
					customer: self.owner.get( 'id' ),
					roles   : [ 'CUSTOMER', ],
				} );
			}
			
			self.dispatch();
		} );
		
		// add the customer layout.
		let customerContainer = new CustomerContainer( {
			view      : view,
			migrate   : true,
			customer  : customer,
			userId    : self.selectedUser.get( 'id' ),
			user      : self.selectedUser,
			context   : 'customer',
			create    : true,
			customers : self.customers,
			navHandler: 'internal',
		} );
		
		// add to viewport.
		self.showChildView( 'container', customerContainer );
	},
	
	// migration
	
	/**
	 * Shows a migrate/wait screen while waiting for migration.
	 * @void
	 */
	_migrateUnit: function () {
		this.trigger( 'migrate', this.legacyUser, this.legacyUnit, this.selectedUser, this.owner );
	},
	
} );

export default LegacyFlow;